import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  MdChatBubbleOutline,
  MdSend,
  MdRemove,
} from "react-icons/md";
import "./AffordabilityAssistant.css";

const GREETING = {
  id: "greeting",
  text:
    "Hello! I'm your Affordability Assistant. I can help answer questions about the Electronic Prior Authorization process. How can I assist you today?",
  sender: "bot",
  timestamp: new Date(),
};

const QUICK_SUGGESTIONS = [
  {
    id: "sources",
    label: "Show sources for all fields",
    userText: "Show sources for all fields",
    botText:
      'A) Structured chart data (highest confidence): Answers pulled directly from discrete fields in the chart—problem list, medication list (active + historical where available), allergies, vitals, labs with dates, diagnoses (ICD-10), and encounter dates—are labeled "EHR — structured."\n\nB) Clinical documentation (medium confidence)\n\nC) Payer form logic (no patient source): We have completed PA question definitions (e.g., dose, indication, route) based on the medication and program—labeled "Payer template."',
  },
  {
    id: "prior-therapies",
    label: "Explain prior therapies",
    userText: "Explain prior therapies",
    botText: "Patient failed metformin and phentermine.",
  },
  {
    id: "low-confidence",
    label: "Low-confidence fields",
    userText: "Low-confidence fields",
    botText: "This form has no low confidence fields.",
  },
  {
    id: "after-submit",
    label: "What happens after submit",
    userText: "What happens after submit",
    botText:
      "After you submit the prior authorization, the usual workflow is:\n\n1. Transmission: The PA package is sent to the payer (or routed through the plan's ePA channel / hub, depending on your setup). You'll see a submission confirmation with a transaction ID.\n\n2. Payer processing: The plan reviews against their criteria. Timing varies by payer and urgency, but many plans return an initial determination within their standard SLA (your admin team can track this in the PA work queue).\n\n3. Determination: You'll receive a status update such as Approved, Denied, or More information needed. If additional info is requested, we'll surface the payer's questions so you can respond quickly.\n\n4. Patient / pharmacy impact: If approved, the approval typically attaches to the processed claim at the pharmacy (details depend on payer and pharmacy workflow). If denied, you may see suggested appeal steps based on denial reason codes (when available).",
  },
];

const DUMMY_CONVERSATION = [
  {
    text: "How do you know patient has previously tried Metformin?",
    sender: "user",
  },
  {
    text:
      "Its documented in both the medication history and patient questionnaire that the patient has tried Metformin for weight management/obesity.",
    sender: "bot",
  },
  { text: "What more can I help on today?", sender: "bot" },
  { text: "No thanks.", sender: "user" },
];

const getBotResponse = (userInput) => {
  const input = userInput.toLowerCase();

  if (input.includes("show sources for all fields")) {
    return QUICK_SUGGESTIONS[0].botText;
  }
  if (input.includes("explain prior therapies")) {
    return QUICK_SUGGESTIONS[1].botText;
  }
  if (input.includes("low-confidence fields")) {
    return QUICK_SUGGESTIONS[2].botText;
  }
  if (input.includes("what happens after submit")) {
    return QUICK_SUGGESTIONS[3].botText;
  }
  if (input.includes("what") && (input.includes("epa") || input.includes("prior auth"))) {
    return "Electronic Prior Authorization (ePA) is a digital process that allows healthcare providers to request approval from insurance companies for medications. It streamlines the approval process and reduces paperwork.";
  }
  if (input.includes("how") && input.includes("submit")) {
    return 'To submit an EPA request: 1) Complete the prescription details, 2) Add any required clinical information, 3) Click "Review & Submit", 4) Verify all information is correct, 5) Click "Send To Pharmacy". You\'ll receive a confirmation once submitted.';
  }
  if (input.includes("status") || input.includes("track")) {
    return 'You can track your EPA request status in the Task List. Each task card displays the current status: "Ready for review", "Requested", "Submitted", "Approved", or "Denied".';
  }
  if (input.includes("coupon") || input.includes("savings")) {
    return "Medication coupons and savings cards can significantly reduce patient costs. When available, they'll appear in the prescription confirmation flow. You can email or SMS these directly to patients.";
  }
  if (input.includes("time") || input.includes("long")) {
    return "EPA processing times vary by insurance provider. Most requests are processed within 24-48 hours. Urgent requests may be expedited. You'll receive notifications when the status changes.";
  }
  if (input.includes("help") || input.includes("support")) {
    return "I can help with: EPA submission process, tracking authorization status, understanding medication coupons, insurance compatibility checks, and general workflow questions. What would you like to know more about?";
  }

  return "I understand you have a question about the EPA process. Could you please provide more details? You can ask about: EPA submission, status tracking, coupons, processing times, or general workflow questions.";
};

const formatTime = (date) =>
  date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });

const AffordabilityAssistant = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [dummyChatIndex, setDummyChatIndex] = useState(0);
  const [inputValue, setInputValue] = useState("");
  const messagesEndRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  useEffect(() => {
    if (isExpanded && !isReady) {
      const timer = setTimeout(() => setIsReady(true), 300);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isExpanded, isReady]);

  const appendMessage = (msg) => setMessages((prev) => [...prev, msg]);

  const handleInputClick = () => {
    if (isReady && isExpanded && dummyChatIndex < DUMMY_CONVERSATION.length) {
      const next = DUMMY_CONVERSATION[dummyChatIndex];
      appendMessage({
        id: `dummy-${Date.now()}-${dummyChatIndex}`,
        text: next.text,
        sender: next.sender,
        timestamp: new Date(),
      });
      setDummyChatIndex((i) => i + 1);
    }
  };

  const handleSendMessage = () => {
    const trimmed = inputValue.trim();
    if (!trimmed) return;

    const userMessage = {
      id: `u-${Date.now()}`,
      text: trimmed,
      sender: "user",
      timestamp: new Date(),
    };
    appendMessage(userMessage);
    setInputValue("");

    setTimeout(() => {
      appendMessage({
        id: `b-${Date.now()}`,
        text: getBotResponse(trimmed),
        sender: "bot",
        timestamp: new Date(),
      });
    }, 1000);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleSuggestionClick = (s) => {
    const now = Date.now();
    const userMessage = {
      id: `u-${now}`,
      text: s.userText,
      sender: "user",
      timestamp: new Date(),
    };
    const botMessage = {
      id: `b-${now + 1}`,
      text: s.botText,
      sender: "bot",
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMessage, botMessage]);
  };

  if (!isExpanded) {
    return (
      <button
        type="button"
        className="aa-fab"
        onClick={() => setIsExpanded(true)}
        aria-label="Open Affordability Assistant"
      >
        <MdChatBubbleOutline className="aa-fab-icon" />
        <span className="aa-fab-label">Affordability Assistant</span>
      </button>
    );
  }

  return (
    <div className="aa-panel" role="dialog" aria-label="Affordability Assistant">
      <div className="aa-panel-header">
        <div className="aa-panel-header-left">
          <div className="aa-panel-header-icon">
            <MdChatBubbleOutline />
          </div>
          <div>
            <h3 className="aa-panel-title">Affordability Assistant</h3>
            <p className="aa-panel-subtitle">Always here to help</p>
          </div>
        </div>
        <button
          type="button"
          className="aa-minimize-btn"
          onClick={() => setIsExpanded(false)}
          aria-label="Minimize chat"
        >
          <MdRemove />
        </button>
      </div>

      <div className="aa-messages">
        {messages.map((m) => (
          <div
            key={m.id}
            className={`aa-message-row ${
              m.sender === "user" ? "aa-row-user" : "aa-row-bot"
            }`}
          >
            <div
              className={`aa-bubble ${
                m.sender === "user" ? "aa-bubble-user" : "aa-bubble-bot"
              }`}
            >
              <p className="aa-bubble-text">{m.text}</p>
              <span className="aa-bubble-time">{formatTime(m.timestamp)}</span>
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      <div className="aa-suggestions">
        {QUICK_SUGGESTIONS.map((s) => (
          <button
            key={s.id}
            type="button"
            className="aa-suggestion-pill"
            onClick={() => handleSuggestionClick(s)}
          >
            {s.label}
          </button>
        ))}
      </div>

      <div className="aa-input-row">
        <input
          type="text"
          className="aa-input"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyPress={handleKeyPress}
          onClick={handleInputClick}
          placeholder="Ask about EPA process..."
        />
        <button
          type="button"
          className="aa-send-btn"
          onClick={handleSendMessage}
          disabled={!inputValue.trim()}
          aria-label="Send message"
        >
          <MdSend />
        </button>
      </div>
    </div>
  );
};

export default AffordabilityAssistant;
